/**
 * RoleGate + ForbiddenScreen — pre-flight role gating (plan Phase F).
 *
 * Today, `App.tsx` falls back to HomeView if a non-admin navigates to
 * `/admin` — silent, confusing, and hard to debug. This primitive
 * renders an explicit forbidden screen instead, so the user sees
 * *why* they can't enter the route + gets a clear way back.
 *
 * Usage inside App.tsx router:
 *
 *   case 'admin':
 *     return (
 *       <RoleGate
 *         route={ROUTES.admin}
 *         user={userData}
 *         onHome={() => handleTabChange('home')}
 *       >
 *         <LazyShell scope="Admin">
 *           <AdminMentorshipView user={userData!} />
 *         </LazyShell>
 *       </RoleGate>
 *     );
 *
 * For access-flag gates (paywall) the module's PaywallGate still owns
 * that UI — see `PaywallGate` in `modules/user/paywall`.
 */

import type { ReactNode } from 'react';
import { Lock, ArrowLeft } from 'lucide-react';
import type { BackendUserWithPlan } from '../../userService';
import {
    canSeeRoute,
    resolveRouteLabel,
    type RouteDef,
} from '../routing/routes';
import SurfaceHeader from '../layout/SurfaceHeader';
import { useScrollFade } from '../ui/useScrollFade';
import './RoleGate.css';

type Props = {
    route: RouteDef;
    user: BackendUserWithPlan | null | undefined;
    /** Called when the user clicks "Return home" in the forbidden
     *  screen. The parent updates `activeTab` so they leave the dead
     *  end cleanly. */
    onHome: () => void;
    /** Shell hamburger trigger — threaded through to SurfaceHeader so
     *  the mobile drawer still opens on the forbidden screen. */
    onMenuClick?: (() => void) | undefined;
    /** Gate hamburger visibility. Defaults to true. */
    showMenu?: boolean | undefined;
    children: ReactNode;
};

/**
 * Gate component — renders children only when the current user has
 * permission to see the route. Otherwise renders ForbiddenScreen.
 */
export function RoleGate({ route, user, onHome, onMenuClick, showMenu, children }: Props) {
    if (canSeeRoute(route, user)) {
        return <>{children}</>;
    }
    return (
        <ForbiddenScreen
            label={resolveRouteLabel(route, user)}
            onHome={onHome}
            onMenuClick={onMenuClick}
            showMenu={showMenu}
        />
    );
}

/**
 * Explicit dead-end screen. Wraps the forbidden message in the same
 * `.view-container` + `<SurfaceHeader>` chrome every other surface
 * uses so the app stays visually consistent even on permission
 * failures.
 */
function ForbiddenScreen({
    label,
    onHome,
    onMenuClick,
    showMenu,
}: {
    label: string;
    onHome: () => void;
    onMenuClick?: (() => void) | undefined;
    showMenu?: boolean | undefined;
}) {
    const { ref: bodyFadeRef } = useScrollFade<HTMLDivElement>({
        axis: 'y', fadeSize: 'md',
    });
    return (
        <div className="view-container role-gate">
            <SurfaceHeader
                title={label}
                onMenuClick={onMenuClick}
                showMenu={showMenu}
            />
            <div ref={bodyFadeRef} className="role-gate__body">
                <section className="role-gate__forbidden" role="alert">
                    <div className="role-gate__forbidden-icon" aria-hidden="true">
                        <Lock size={28} />
                    </div>
                    <h2 className="role-gate__forbidden-title">
                        {label} is off-limits for your role
                    </h2>
                    <p className="role-gate__forbidden-body">
                        Your current permissions don't include this section.
                        If you think that's a mistake, ask an admin to grant
                        access.
                    </p>
                    <button
                        type="button"
                        className="role-gate__forbidden-btn"
                        onClick={onHome}
                    >
                        <ArrowLeft size={16} aria-hidden="true" />
                        Return home
                    </button>
                </section>
            </div>
        </div>
    );
}
